import { trackEvent } from '@patrianna/analytics'
import {
  cleanDialogStack,
  closeDialogByName,
  getDialogStack,
  getDialogVisibilityByName,
  openDialog,
  replaceDialog,
} from '@patrianna/dialog-manager'
import { scriptInitialization } from '@patrianna/shared-utils'
import { detectMobile } from '@patrianna/shared-utils/helpers'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import {
  fetchSavedCards,
  getPurchaseHistory,
  repeatPaymentAfterErrorByProvider,
  setIsInsufficientFundsOffersAvailable,
  setIsPaymentFlowInProcess,
  setQuickPurchaseStage,
  showResquePaymentMethod,
} from '@patrianna-payments/shared-store/payments/store/actions'
import {
  offerDeclineTrustlyHandler,
  paymentErrorsHandler,
  quickPurchasePaymentErrorsHandler,
} from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import {
  getPurchaseProvidersSelector,
  getTrustlyConfigSelector,
} from '@patrianna-payments/shared-store/payments/store/selectors'
import { convertOpenApiErrorResponseToGateway } from '@patrianna-payments/shared-store/payments/utils/convertOpenApiErrorResponseToGateway'
import { closeAllDialogs } from '@patrianna-payments/shared-store/redeem/store/middleware-actions'
import * as Sentry from '@sentry/nextjs'

import { setClarity } from 'analyticActions/clarity/events'
import { trackClickOnOffer, trackSuccessPayment } from 'analyticActions/common'
import { trackFirstPurchase } from 'analyticActions/common/trackFirstPurchase'
import { trackSPPaymentFailed } from 'analyticActions/common/trackPaymentSnowplow'
import ROUTES from 'config/routes.json'
import { ApplePayGooglePayProviders } from 'dialogs/PaymentDialog/PaymentWays/type'
import {
  getDateRangeFromQuery,
  getDateRangeUTC,
  getValidatedDateRange,
} from 'pageContainers/TransactionsPage/components/FiltersBlock/utils'
import { FIRST_PAGE, PAGE_SIZE } from 'pageContainers/TransactionsPage/components/PaymentsTab'
import content from 'prismic-translates/dialog-order-confirmation-message.json'
import { getSeonSessionId } from 'services/seon'
import RepeatPaymentSnackbar from 'src/containers/GlobalSnackbar/RepeatPaymentSnackbar'
import { OFFER_TEMP } from 'src/dialogs/PaymentDialog/PaymentWays/Buttons/constants'
import { OFFER_CODE_VERIFICATION } from 'src/hooks/useReturnAfterVerification'
import { serviceDependencies } from 'src/services'
import { jumioKycAddressSelector } from 'src/store/modules/kyc/selectors'
import { getAvailablePaymentProvidersRequest } from 'store/modules/appConfig/actions'
import { getOffers } from 'store/modules/shop/actions'
import { getOfferByCodeSelector, purchaseLimitsSelector } from 'store/modules/shop/selectors'
import { clearSlotGameFlow } from 'store/modules/slotGameFlow/actions'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { getAccountInfo, getLiveChatSettingsRequest, setFirstDepositDate } from 'store/modules/user/actions'
import {
  getLiveChatSettingsSelector,
  getUserAccountIdSelector,
  getUserHashSelector,
  getUserRestrictionsSelector,
  getUserSelector,
  isLoggedInSelector,
} from 'store/modules/user/selectors'
import { getDataFromSessionStorage, removeDataFromSessionStorage } from 'utils/sessionStorage'

import type { OfferDeclineNotification } from '@patrianna/shared-patrianna-types/websocket/notifications'
import type { CreateOrderResponse } from '@patrianna/shared-patrianna-types/websocket/response'
import type { WSResponse } from '@patrianna/shared-patrianna-types/websocket/types'
import type {
  BuyShopOfferProps,
  CreateOrderResponseCardPaymentErrorProps,
  OfferDeclineTrustlyProps,
  OpenRemoveAccountPaymentMethodsDialogProps,
  ThreeDSCheckingType,
} from '@patrianna-payments/shared-store/payments/store/types'
import type { ConfirmOrderResponseBody } from '@patrianna-payments/shared-store/payments/types/openApiModels/ConfirmOrderResponseBody'
import type { CreateOrderResponseBody } from '@patrianna-payments/shared-store/payments/types/openApiModels/CreateOrderResponseBody'
import type { GetPaymentOrderRequestBody } from '@patrianna-payments/shared-store/payments/types/openApiModels/GetPaymentOrderRequestBody'
import type { OfferInfo } from '@patrianna-payments/shared-store/payments/types/openApiModels/OfferInfo'
import type { RescueProviders } from 'dialogs/PaymentDialog/PaymentWays/type'
import type { TypedThunk } from 'src/store'

const CARD_ERROR_51 = '51'
const ERR_CARD_BLOCKED = 'err_card_blocked'

const t = getTranslationFromJson([content])

//['PAYMENT_DIALOG', 'PAYMENT_CVV_DIALOG', 'PAYMENT_THREE_DS_DIALOG', 'SHOP_DIALOG']
const checkAndRemovePaymentDialogsFromStack =
  (closeDialogs: Array<string>): TypedThunk =>
  () => {
    closeDialogs.forEach((dialog) => closeDialogByName({ dialogName: dialog }))
  }

export const handleFinishedPaymentFlowAction =
  ({ transactionId }: { transactionId?: string }): TypedThunk =>
  (dispatch) => {
    const dialogStack = getDialogStack()
    const confirmationSuccessMessage = dialogStack?.find?.((el) => {
      // @ts-ignore
      return el.dialogName === 'ORDER_CONFIRMATION_MESSAGE' && el.dialogProps?.status === 'success'
    })

    dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
    dispatch(
      checkAndRemovePaymentDialogsFromStack([
        'PAYMENT_DIALOG',
        'PAYMENT_CVV_DIALOG',
        'PAYMENT_THREE_DS_DIALOG',
        'SHOP_DIALOG',
      ]),
    )

    if (!confirmationSuccessMessage) {
      replaceDialog({ dialogName: 'ORDER_CONFIRMATION_MESSAGE', dialogProps: { status: 'waiting', transactionId } })
    }
  }

const showRescueProviders =
  ({
    code,
    errorMessage = 'Payment has failed',
    providers,
    loadPaymentData = true,
    errorCode,
  }: {
    code: string
    errorMessage: string
    providers: string[]
    loadPaymentData?: boolean
    errorCode?: string
  }): TypedThunk =>
  (dispatch) => {
    const isMobile = detectMobile()

    dispatch(
      checkAndRemovePaymentDialogsFromStack([
        'PAYMENT_DIALOG',
        'PAYMENT_CVV_DIALOG',
        'PAYMENT_THREE_DS_DIALOG',
        'ORDER_CONFIRMATION_MESSAGE',
      ]),
    )

    // don't show rescue providers dialog on desktop and if all rescue providers are apple pay or google pay
    if (!isMobile && providers.every((provider) => ApplePayGooglePayProviders.includes(provider as RescueProviders))) {
      dispatch(openSnackbar({ message: errorMessage }))
    } else {
      dispatch(showResquePaymentMethod(true))
      openDialog({
        dialogName: 'PAYMENT_DIALOG',
        dialogProps: { code, rescueProviders: { errorCode, errorMessage, providers }, loadPaymentData },
      })
    }
  }

export const handleFinishedQuickPurchasePaymentFlowAction =
  (res: CreateOrderResponseBody | ConfirmOrderResponseBody): TypedThunk =>
  (dispatch, getState) => {
    const offer = getOfferByCodeSelector(getState(), res.offer.code)

    dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
    dispatch(
      setQuickPurchaseStage({ stage: 'success_purchase', params: { billingDescriptor: res.billingDescriptor, offer } }),
    )

    const dialogStack = getDialogStack()
    const quickPurchaseDialog = dialogStack?.find?.((el) => el.dialogName === 'QUICK_PURCHASE_DIALOG')

    if (!quickPurchaseDialog) {
      replaceDialog({ dialogName: 'QUICK_PURCHASE_DIALOG' })
    }

    dispatch(handleOfferQuickPurchaseSuccessAction(res))
  }

export const handleOfferQuickPurchaseSuccessAction =
  (response: CreateOrderResponseBody): TypedThunk =>
  (dispatch) => {
    dispatch(handleOfferPurchaseSuccessAction(response, true))
  }

export const handleOfferPurchaseSuccessAction =
  (response: CreateOrderResponseBody, isQuickPurchase?: boolean): TypedThunk =>
  (dispatch, getState) => {
    dispatch(showResquePaymentMethod(false))
    const {
      provider,
      billingDescriptor,
      offer: { code },
      method,
    } = response
    const user = getUserSelector(getState())
    const kycInfo = jumioKycAddressSelector(getState())
    const userHash = getUserHashSelector(getState())
    const liveChatSettings = getLiveChatSettingsSelector(getState())
    const offer = getOfferByCodeSelector(getState(), code)

    dispatch(getOffers())

    closeDialogByName({ dialogName: 'PAYMENT_CVV_DIALOG' })

    if (!isQuickPurchase) {
      dispatch(checkAndRemovePaymentDialogsFromStack(['PAYMENT_DIALOG', 'PAYMENT_THREE_DS_DIALOG', 'SHOP_DIALOG']))
      dispatch(setIsInsufficientFundsOffersAvailable(true))
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))

      replaceDialog({
        dialogName: 'ORDER_CONFIRMATION_MESSAGE',
        dialogProps: {
          status: 'success',
          provider,
          billingDescriptor,
          method,
          offer,
          offerCode: code,
        },
      })
    }

    dispatch(fetchSavedCards())
    dispatch(getAccountInfo())

    trackSuccessPayment(response, user, kycInfo, userHash)

    dispatch(setIsInsufficientFundsOffersAvailable(true))

    if (!liveChatSettings.showLiveChat) {
      dispatch(getLiveChatSettingsRequest())
    }

    if (response.isFirstDeposit) {
      if ('firstDepositDate' in response) {
        dispatch(setFirstDepositDate({ firstDepositDate: response.firstDepositDate }))
      }

      dispatch(getAvailablePaymentProvidersRequest())

      trackFirstPurchase({
        currency: response.currency,
        offerCode: response.offer.code,
        city: response?.city || kycInfo?.city || user?.softKycInfo?.city,
        zip: response?.zip || kycInfo?.zip || user?.softKycInfo?.zip,
        external_id: userHash,
        transactionId: response.transactionId,
        value: response.amount,
        transactionTotal: response.offer.price,
        provider: response.provider,
        userId: user?.id || '',
      })
    }

    if (window.location.href.includes('/play')) {
      dispatch(clearSlotGameFlow())
    }
  }

export const handlePaymentErrorsHandlerAction =
  (error: WSResponse, isQuickPurchase?: boolean): TypedThunk =>
  (dispatch) => {
    if (!error?.status) {
      if (process.env.ENABLE_LOG_MESSAGES) {
        Sentry.captureMessage(`non gateway error ${JSON.stringify(error)}`)
      }
    } else {
      const { status, body } = error
      const offerCode = getDataFromSessionStorage<string>(OFFER_CODE_VERIFICATION)
      const isOtpRequired = status?.errorCode === 'err_otp_required'

      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))

      trackSPPaymentFailed({
        transactionId: (body as CreateOrderResponseBody)?.transactionId,
        errorCode: status?.errorCode,
        errorMessage: status?.errorText,
      })

      // close payment dialogs
      dispatch(
        checkAndRemovePaymentDialogsFromStack([
          'PAYMENT_DIALOG',
          'PAYMENT_CVV_DIALOG',
          'PAYMENT_THREE_DS_DIALOG',
          'ORDER_CONFIRMATION_MESSAGE',
        ]),
      )

      if ('isFirstDeposit' in body && body.isFirstDeposit) {
        dispatch(getAvailablePaymentProvidersRequest())
      }

      if (body?.type?.includes('Capture3DsPaymentOrderResponse') && status.errorCode?.includes('err_3ds_failed')) {
        dispatch(
          openSnackbar({
            message: error.status.errorText,
            componentProps: { message: status.errorText, offerCode },
            errorDetails: { errorCode: status?.errorCode },
            autoHide: 15000,
          }),
        )

        return null
      }

      switch (body?.type) {
        case 'CreateOrderResponse': {
          void trackEvent('submitted_payment_error', {
            category: 'CreateOrderRequest',
            label: status?.errorCode || status?.errorText,
          })

          if (body.requestKyc) {
            openDialog({ dialogName: 'GET_VERIFIED_DIALOG' })
          }

          break
        }
        case 'RepeatPaymentOrderResponse': {
          void trackEvent('submitted_payment_error', {
            category: 'RepeatPaymentOrderResponse',
            label: 'RepeatPaymentOrderResponse',
          })
          break
        }
        default: {
          console.log('payment error', error)
        }
      }

      if (status?.errorText?.includes?.('Transaction already processed')) {
        setClarity('duplicate_transaction', `Transaction already processed req: ${body.type}`)
      }

      if (isOtpRequired) {
        window.location.href = ROUTES.OTP
      }

      const isCreateOrderResponse = body?.type === 'CreateOrderResponse'

      const createOrderBody = body as CreateOrderResponseBody
      const binAttributeErrorMessage = createOrderBody?.error?.message
      const hasRescueProviders = (createOrderBody?.error?.rescueProviders?.length ?? 0) > 0
      const isCardBlockedError =
        isCreateOrderResponse &&
        (status?.errorCode as string) === ERR_CARD_BLOCKED &&
        Boolean(binAttributeErrorMessage) &&
        !hasRescueProviders

      const isLimitError = () => {
        /*
         * BUG: JavaScript uses the IEEE 754 standard for representing numbers. In this format, some decimal fractions
         * FIX: Convert amount in cents (integers)
         */
        const { limitAvailable, price } = body as CreateOrderResponse
        const fixedPrice = Math.round(Number(price) * 100)
        const fixedLimitAvailable = Math.round(Number(limitAvailable ?? Infinity) * 100)

        return (
          (limitAvailable != null && fixedPrice > fixedLimitAvailable) ||
          (limitAvailable && status?.errorCode === 'err_payment_purchase_limit')
        )
      }

      if (
        isCreateOrderResponse &&
        body?.error?.cardNetworkError === CARD_ERROR_51 &&
        !['err_insufficient_funds'].includes(status?.errorCode)
      ) {
        replaceDialog({
          dialogName: 'PAYMENT_ERROR_DIALOG',
          dialogProps: { title: 'Hmmm. Not enough funds available', reason: error.status.errorText },
        })
      } else if (isCreateOrderResponse && status?.errorCode === 'err_cardholder_name_mismatch') {
        openDialog({
          dialogName: 'PAYMENT_ERROR_DIALOG',
          dialogProps: { title: 'Transaction Declined!', reason: status?.errorText },
        })
      } else if (isCreateOrderResponse && isLimitError()) {
        openDialog({
          dialogName: 'PURCHASE_LIMIT_DIALOG',
          dialogProps: {
            limitAmount: body.limitAmount,
            limitAvailable: body.limitAvailable,
            limitPeriod: body.limitPeriod,
            limitEnd: body.limitEnd,
          },
        })
        if (status?.errorText && isQuickPurchase) {
          dispatch(
            setQuickPurchaseStage({
              stage: 'failed_purchase',
              params: { error: status.errorText, errorCode: status.errorCode },
            }),
          )
        } else if (isQuickPurchase) {
          dispatch(setQuickPurchaseStage({ stage: 'quick_purchase', params: null }))
        }
        // add condition for capture 3ds response
      } else if (isCardBlockedError) {
        if (isQuickPurchase) {
          dispatch(
            setQuickPurchaseStage({
              stage: 'failed_purchase',
              params: { error: binAttributeErrorMessage, errorCode: status?.errorCode },
            }),
          )
        } else if (!getDialogVisibilityByName('PAYMENT_ERROR_DIALOG')) {
          openDialog({
            dialogName: 'PAYMENT_ERROR_DIALOG',
            dialogProps: { offer: createOrderBody.offer, reason: binAttributeErrorMessage },
          })
        }
      } else if (status?.errorText) {
        if (isQuickPurchase) {
          if (isCreateOrderResponse && (body.limitAmount || body.requestKyc)) {
            closeDialogByName({ dialogName: 'QUICK_PURCHASE_DIALOG' })
            dispatch(setQuickPurchaseStage({ stage: 'quick_purchase', params: null })) // [FYI]: reset quick purchase flow
          } else {
            dispatch(
              setQuickPurchaseStage({
                stage: 'failed_purchase',
                params: { error: status.errorText, errorCode: status.errorCode },
              }),
            )
          }
        } else {
          // 'err_kyc_required' / 'err_kyc_required_low_risk' / 'err_kyc_required_high_risk'
          const shouldShowSnackbar = !status?.errorCode.startsWith('err_kyc_required')
          const shouldRedirectToPurchase = ![
            'err_not_found',
            'err_payment_in_progress',
            'err_payment_purchase_limit',
          ].includes(status?.errorCode)

          if (shouldShowSnackbar) {
            if (isCreateOrderResponse && status?.errorCode === 'err_insufficient_funds') {
              openDialog({
                dialogName: 'INSUFFICIENT_FUNDS',
                dialogProps: { message: status?.errorText, offerCode: body?.offer?.code },
              })
            } else if (isCreateOrderResponse && body?.error?.rescueProviders?.length > 0) {
              dispatch(setIsInsufficientFundsOffersAvailable(true))
              dispatch(
                showRescueProviders({
                  code: body?.offer?.code,
                  errorMessage: status.errorText,
                  providers: body?.error?.rescueProviders,
                  loadPaymentData: false,
                  errorCode: status?.errorCode,
                }),
              )
            } else if (shouldRedirectToPurchase) {
              dispatch(setIsInsufficientFundsOffersAvailable(true))
              dispatch(
                openSnackbar({
                  message: RepeatPaymentSnackbar,
                  componentProps: { message: status.errorText, offerCode },
                  errorDetails: { errorCode: status?.errorCode },
                  autoHide: 15000,
                }),
              )
            } else {
              dispatch(setIsInsufficientFundsOffersAvailable(true))
              dispatch(
                openSnackbar({
                  message: status.errorText,
                  errorDetails: { errorCode: status?.errorCode },
                  autoHide: 15000,
                }),
              )
            }
          }
        }
      }
    }

    return null
  }

export const handleQuickPurchasePaymentErrorsHandlerAction =
  (error: WSResponse): TypedThunk =>
  (dispatch) => {
    dispatch(handlePaymentErrorsHandlerAction(error, true))
  }

export const handleCreateOrderResponseCardQuickPurchasePaymentErrorHandlerAction =
  (props: CreateOrderResponseCardPaymentErrorProps): TypedThunk =>
  async (dispatch, getState) => {
    const { error, options } = props
    const accountId = getUserAccountIdSelector(getState())
    const errorCode = error?.status?.errorCode
    const dialogStack = getDialogStack()
    const quickPurchaseDialog = dialogStack?.find?.((el) => el.dialogName === 'QUICK_PURCHASE_DIALOG')

    if (!quickPurchaseDialog) {
      replaceDialog({ dialogName: 'QUICK_PURCHASE_DIALOG' })
    }

    const trackErrorEvent = () => {
      void trackEvent('QuickPurchaseFailed', {
        category: options.offerCode,
        label: `Provider - ${error.body.provider || 'empty'}`, // [FYI]: missing - get from BE as ""
        user_id: accountId,
        transactionTotal: error.body.price,
        error: errorCode || error?.status?.errorText,
      })
    }

    if (errorCode === 'err_payment_input_cvv') {
      trackErrorEvent()
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
      closeDialogByName({ dialogName: 'QUICK_PURCHASE_DIALOG' })
      dispatch(setQuickPurchaseStage({ stage: 'quick_purchase' }))
      openDialog({
        dialogName: 'PAYMENT_CVV_DIALOG',
        dialogProps: {
          ...(options.isCustomProvider ? { provider: options.provider } : {}),
          cardToken: options.paymentMethodId,
          offerCode: options.offerCode,
          sourceId: error.body.sourceId,
          isQuickPurchase: true,
        },
      })
    } else if (errorCode === 'err_payment_routing') {
      trackErrorEvent()

      const seonSessionId = await getSeonSessionId()

      dispatch(repeatPaymentAfterErrorByProvider({ ...props, seonSessionId, isQuickPurchase: true }))
    } else if (errorCode === 'err_payment_3ds_required') {
      const seonSessionId = await getSeonSessionId()

      dispatch(
        repeatPaymentAfterErrorByProvider({ ...props, seonSessionId, isThreeDSRequired: true, isQuickPurchase: true }),
      )
    } else if (errorCode === 'err_3ds_failed') {
      trackSPPaymentFailed({
        transactionId: error?.body?.transactionId,
        errorCode: error?.status?.errorCode,
        errorMessage: error?.status?.errorText,
      })
      closeDialogByName({ dialogName: 'PAYMENT_THREE_DS_DIALOG' })
      dispatch(setQuickPurchaseStage({ stage: 'failed_purchase', params: { error: error?.status?.errorText } }))
    } else if (error instanceof TypeError) {
      trackSPPaymentFailed({
        errorCode: 'network_error',
        errorMessage: t('your_purchase_network_failed'),
      })
      dispatch(closeAllDialogs())
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
      dispatch(openSnackbar({ message: t('your_purchase_network_failed') }))
    } else {
      dispatch(quickPurchasePaymentErrorsHandler(error))
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
    }
  }

export const handlePurchaseResponseListenerByRouterAction = (): TypedThunk => (dispatch, getState) => {
  const query = new URLSearchParams(window.location.search)
  const user = getUserSelector(getState())
  const kycInfo = jumioKycAddressSelector(getState())
  const userHash = getUserHashSelector(getState())
  const liveChatSettings = getLiveChatSettingsSelector(getState())

  if (query.get('payment_status')) {
    if (query.get('payment_status') === 'success') {
      if (
        query.get('transaction_id') &&
        query.get('amount') &&
        query.get('description') &&
        query.get('offer') &&
        query.get('currency')
      ) {
        const response = {
          provider: query.get('originalProvider'),
          transactionId: query.get('transaction_id'),
          currency: query.get('currency'),
          offer: {
            price: Number(query.get('amount')),
            title: query.get('description'),
            code: query.get('offer'),
          },
          city: kycInfo?.city || user?.softKycInfo?.city,
          zip: kycInfo?.zip || user?.softKycInfo?.zip,
        }

        trackSuccessPayment(response, user, kycInfo, userHash)

        const data: GetPaymentOrderRequestBody = {
          transactionId: response.transactionId,
        }

        serviceDependencies.payment
          .getPaymentOrder(data)
          .then(({ provider = '', success = false, offer, method, billingDescriptor }) => {
            // temp solution, remove once BE sent correct offer data in response (GC, SC)
            const offerFromStorage = getDataFromSessionStorage(OFFER_TEMP)
            const offerToDispatch = offerFromStorage || offer

            if (success) {
              replaceDialog({
                dialogName: 'ORDER_CONFIRMATION_MESSAGE',
                dialogProps: {
                  status: 'success',
                  provider,
                  method,
                  offer: offerToDispatch,
                  offerCode: offer?.code,
                  billingDescriptor,
                },
              })

              // temp solution, remove once BE sent correct offer data in response (GC, SC)
              removeDataFromSessionStorage(OFFER_TEMP)

              if (!liveChatSettings.showLiveChat) {
                dispatch(getLiveChatSettingsRequest())
              }

              if (query.get('isFirstDeposit')) {
                dispatch(getAvailablePaymentProvidersRequest())

                trackFirstPurchase({
                  currency: response.currency,
                  offerCode: response.offer.code,
                  city: response.city,
                  zip: response.zip,
                  external_id: userHash,
                  transactionTotal: response.offer.price,
                  transactionId: response?.transactionId,
                  value: response?.offer.price,
                  provider: response?.provider,
                  userId: user?.id || '',
                })
              }
            }
          })
          .catch(convertOpenApiErrorResponseToGateway('GetPaymentOrderResponse'))
          .catch((err) => {
            dispatch(serviceDependencies.errorHandler(err, data))
          })
      }

      replaceDialog({ dialogName: 'ORDER_CONFIRMATION_MESSAGE', dialogProps: { status: 'waiting' } })
    } else if (query.get('payment_status') === 'cancelled') {
      trackSPPaymentFailed({
        transactionId: query.get('transaction_id'),
        errorCode: 'cancelled',
        errorMessage: 'Payment cancelled',
      })
      const data: GetPaymentOrderRequestBody = { transactionId: query.get('transaction_id') }

      serviceDependencies.payment
        .getPaymentOrder(data)
        .then((res) => {
          if (res?.error?.rescueProviders?.length > 0) {
            dispatch(setIsInsufficientFundsOffersAvailable(true))
            dispatch(
              showRescueProviders({
                code: res?.offer?.code,
                errorMessage: res?.failReason,
                providers: res?.error?.rescueProviders,
                errorCode: res?.error?.cardNetworkError,
              }),
            )
          } else {
            dispatch(setIsInsufficientFundsOffersAvailable(true))
            replaceDialog({
              dialogName: 'ORDER_CONFIRMATION_MESSAGE',
              dialogProps: {
                status: 'cancelled',
                provider: query.get('originalProvider') || null,
                offerCode: res?.offer?.code,
              },
            })
          }
        })
        .catch(convertOpenApiErrorResponseToGateway('GetPaymentOrderResponse'))
        .catch((err) => {
          dispatch(serviceDependencies.errorHandler(err, data))
        })
    }

    window.history.replaceState({}, document.title, window.location.href.replace(window.location.search, ''))
  }
}

export const trustlyAction =
  ({
    tempToken,
    offer,
    transactionId,
    code,
  }: {
    tempToken: string
    offer: OfferInfo
    transactionId: string
    code: string
  }): TypedThunk =>
  (dispatch, getState) => {
    const trustlyConfig = getTrustlyConfigSelector(getState())

    dispatch(fetchSavedCards())
    dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))

    const data = {
      accessId: trustlyConfig.id,
      merchantId: trustlyConfig.merchant,
      description: offer.title,
      currency: 'USD',
      amount: '0.00',
      paymentType: 'Verification',
      authToken: 'new',
      merchantReference: transactionId,
      requestSignature: tempToken,
      transactionId: code,
      returnUrl: '#',
      cancelUrl: '#',
      notificationUrl: trustlyConfig.notificationUrl,
    }

    scriptInitialization(`${process.env.TRUSTLY_PWMB}${trustlyConfig.id}`, 'Trustly').then(() => {
      window.Trustly.establish(data)
    })
  }

export const handleOfferDeclineTrustlyHandlerAction =
  ({
    reason,
    offer,
    rescueProviders,
    internalErrorCode,
  }: OfferDeclineTrustlyProps & { internalErrorCode?: string }): TypedThunk =>
  (dispatch) => {
    dispatch(
      checkAndRemovePaymentDialogsFromStack(['PAYMENT_DIALOG', 'PAYMENT_CVV_DIALOG', 'ORDER_CONFIRMATION_MESSAGE']),
    )

    if (internalErrorCode === 'err_insufficient_funds') {
      openDialog({
        dialogName: 'INSUFFICIENT_FUNDS',
        dialogProps: {
          message: reason,
          offerCode: offer?.code,
        },
      })
    } else if (rescueProviders?.length > 0) {
      dispatch(setIsInsufficientFundsOffersAvailable(true))
      dispatch(showRescueProviders({ code: offer?.code, errorMessage: reason, providers: rescueProviders }))
    } else {
      dispatch(setIsInsufficientFundsOffersAvailable(true))
      replaceDialog({ dialogName: 'PAYMENT_ERROR_DIALOG', dialogProps: { offer, reason } })
    }

    // if (provider !== 'trustly') {
    //   dispatch(openSnackbar({ message: reason || 'Payment has failed' }))
    // }
  }

export const handleOfferDeclineNotificationHandlerAction =
  ({
    tempToken,
    reason,
    provider,
    offer,
    transactionId,
    code,
    errCode,
    rescueProviders,
    internalErrorCode,
  }: Omit<OfferDeclineNotification, 'offer'> & {
    // TODO: fix type
    offer: OfferInfo
    internalErrorCode?: string
  }): TypedThunk =>
  (dispatch) => {
    dispatch(fetchSavedCards())
    dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))

    trackEvent('submitted_payment_error', { category: 'CreateOrderRequest', label: reason })

    trackSPPaymentFailed({
      transactionId,
      errorCode: errCode ?? internalErrorCode,
      errorMessage: reason,
    })

    if (provider === 'trustly') {
      dispatch(
        offerDeclineTrustlyHandler({
          tempToken,
          reason,
          offer,
          transactionId,
          code,
          errCode,
          internalErrorCode,
        }),
      )

      return null
    }

    dispatch(checkAndRemovePaymentDialogsFromStack(['PAYMENT_DIALOG', 'PAYMENT_CVV_DIALOG', 'PAYMENT_THREE_DS_DIALOG']))

    if (internalErrorCode === 'err_insufficient_funds') {
      openDialog({
        dialogName: 'INSUFFICIENT_FUNDS',
        dialogProps: { message: reason, offerCode: offer?.code },
      })
    } else if (rescueProviders?.length > 0) {
      dispatch(setIsInsufficientFundsOffersAvailable(true))
      dispatch(
        showRescueProviders({ code: offer.code, errorMessage: reason, providers: rescueProviders, errorCode: errCode }),
      )
    } else {
      dispatch(setIsInsufficientFundsOffersAvailable(true))
      dispatch(openSnackbar({ message: reason || 'Payment has failed' }))
    }

    return null
  }

export const handleOpenPaymentProvidersDialogAction =
  (offer: OfferInfo, snowplowPayFlow): TypedThunk =>
  (dispatch, getState) => {
    const purchaseProviders = getPurchaseProvidersSelector(getState())

    dispatch(showResquePaymentMethod(false))
    dispatch(fetchSavedCards())

    if (!purchaseProviders) {
      dispatch(getAvailablePaymentProvidersRequest())
    }

    // pay with card
    // skrill
    openDialog({
      dialogName: 'PAYMENT_DIALOG',
      dialogProps: { code: offer.code, fetchProvidersAndMethods: false, snowplowPayFlow },
    })
  }

export const handleBuyShopOfferAction =
  ({ offer, feature, routerPush, snowplowPayFlow }: BuyShopOfferProps): TypedThunk =>
  (dispatch, getState) => {
    const purchaseLimits = purchaseLimitsSelector(getState())
    const isLoggedIn = isLoggedInSelector(getState())
    const userRestrictions = getUserRestrictionsSelector(getState())

    trackClickOnOffer(offer, feature)

    if (!isLoggedIn) {
      cleanDialogStack()

      const searchParams = new URLSearchParams()

      if (feature === 'in_store') {
        searchParams?.set('from', window?.location?.pathname)
      }

      return routerPush(`${ROUTES.REGISTER}?${searchParams?.toString()}`)
    }

    if (userRestrictions.includes('no_purchase')) {
      return openDialog({ dialogName: 'RESTRICT_USER_DIALOG' })
    }

    /*
     * BUG: JavaScript uses the IEEE 754 standard for representing numbers. In this format, some decimal fractions cannot be represented exactly in binary.
     * FIX: Convert amount in cents (integers)
     * */
    const offerPrice = Math.round(Number(offer?.price ?? 0) * 100)
    const limitAvailable = Math.round(Number(purchaseLimits?.limitAvailable ?? Infinity) * 100)

    // check for purchase limits
    if (purchaseLimits?.limitAvailable != null && offerPrice > limitAvailable) {
      return openDialog({ dialogName: 'PURCHASE_LIMIT_DIALOG', dialogProps: { ...purchaseLimits } })
    }

    dispatch(handleOpenPaymentProvidersDialogAction(offer, snowplowPayFlow))
  }

export const handleCreateOrderResponseCardPaymentErrorHandlerAction =
  (props: CreateOrderResponseCardPaymentErrorProps): TypedThunk =>
  async (dispatch) => {
    const { error, options } = props

    if (error?.status?.errorCode === 'err_payment_input_cvv') {
      dispatch(
        checkAndRemovePaymentDialogsFromStack([
          'PAYMENT_DIALOG',
          'PAYMENT_THREE_DS_DIALOG',
          'SHOP_DIALOG',
          'ORDER_CONFIRMATION_MESSAGE',
        ]),
      )
      openDialog({
        dialogName: 'PAYMENT_CVV_DIALOG',
        dialogProps: {
          ...(options.isCustomProvider ? { provider: options.provider } : {}),
          cardToken: options.paymentMethodId,
          offerCode: options.offerCode,
          sourceId: error.body.sourceId,
        },
      })
    } else if (error?.status?.errorCode === 'err_payment_routing') {
      const seonSessionId = await getSeonSessionId()

      dispatch(repeatPaymentAfterErrorByProvider({ ...props, seonSessionId }))
    } else if (error?.status?.errorCode === 'err_payment_3ds_required') {
      const seonSessionId = await getSeonSessionId()

      dispatch(repeatPaymentAfterErrorByProvider({ ...props, seonSessionId, isThreeDSRequired: true }))
    } else if (error instanceof TypeError) {
      trackSPPaymentFailed({
        errorCode: 'network_error',
        errorMessage: t('your_purchase_network_failed'),
      })
      dispatch(closeAllDialogs())
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
      dispatch(openSnackbar({ message: t('your_purchase_network_failed') }))
    } else {
      dispatch(paymentErrorsHandler(error))
      dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
    }
  }

export const handle3dsFlowStart =
  (threeDsData: CreateOrderResponseBody & ThreeDSCheckingType): TypedThunk =>
  (dispatch) => {
    dispatch(setIsPaymentFlowInProcess({ isPaymentFlowInProcess: false }))
    cleanDialogStack()
    openDialog({ dialogName: 'PAYMENT_THREE_DS_DIALOG', dialogProps: threeDsData })
  }

export const getPurchaseHistoryWithDateRage = (): TypedThunk => (dispatch) => {
  if (window?.location?.pathname === ROUTES.TRANSACTIONS) {
    dispatch(
      getPurchaseHistory({
        dateRange: getDateRangeUTC(getValidatedDateRange(getDateRangeFromQuery(window?.location?.search))),
        pageRequest: { pageNumber: FIRST_PAGE, pageSize: PAGE_SIZE },
      }),
    )
  }
}

export const handleOpenRemoveAccountPaymentMethodsDialogAction =
  (dialogProps: OpenRemoveAccountPaymentMethodsDialogProps): TypedThunk =>
  () => {
    if (dialogProps) {
      openDialog({
        dialogName: 'DELETE_PAYMENT_METHOD_DIALOG',
        dialogProps,
      })
    }
  }
